import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { ReportTemplateListItem, ReportTemplateResponse, TemplateOrderBy, ValidateTemplateConfigResponse } from 'models'
import { ReportTemplateService, TenantServiceV3, FrameworkServiceV3, CpiServiceV3 } from 'services'
import { useAppDispatch } from 'store/hooks'
import { openToast, closeToast, ToastVariant } from 'store/toasts/toastSlice'
import TemplateSortSelect from '../../components/template-sort-select/TemplateSortSelect'
import TrendsChartSelectField from 'ui/pages/cpi-details/sections/trends-tab/components/TrendsChartSelectField'
import SearchInput from 'ui/components/form-components/SearchInput'
import useDebounce from 'hooks/useDebounce'
import ReportCardV2 from '../../components/report-card-v2/ReportCardV2'
import TemplateOptionsMenu from '../../components/template-options-menu/TemplateOptionsMenu'
import CustomizeReportDialog from '../../components/customize-report-dialog/CustomizeReportDialog'
import DeleteTemplateDialog from '../../components/delete-template-dialog/DeleteTemplateDialog'
import DuplicateTemplateDialog from '../../components/duplicate-template-dialog/DuplicateTemplateDialog'
import ScheduleReportDialog from '../../components/schedule-report-dialog/ScheduleReportDialog'
import StopScheduleConfirmationDialog from '../../components/stop-schedule-confirmation-dialog/StopScheduleConfirmationDialog'
import { generateAndDownloadFromTemplate, getApiErrorDescription } from '../../reports-v2.utils'
import { useAuth } from '@frontegg/react'
import { hasAnyRole, hasPermission as userHasPermission, Permission, Role } from 'lib/utils'
import { PerformanceReportIcon } from 'ui/components/svg'
import TemplatesEmptyState, { TemplatesEmptyStateVariant } from './TemplatesEmptyState'
import ViewTemplateDrawer from '../../components/view-template-drawer/ViewTemplateDrawer'
import {
  CustomTemplatesContainer,
  SectionTitle,
  SearchRow,
  SortRow,
  ItemCountLabel,
  ItemCountValue,
  TemplateCardsContainer
} from './customTemplates.styles'

const formatDate = (dateString?: string | null): string | null => {
  if (!dateString) return null
  const date = new Date(dateString)
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = date.getFullYear()
  return `${month}/${day}/${year}`
}

const SORT_OPTIONS = ['A to Z', 'Z to A', 'Latest Created', 'Oldest Created']

const SORT_MAP: Record<string, TemplateOrderBy> = {
  'A to Z': TemplateOrderBy.NAME_ASC,
  'Z to A': TemplateOrderBy.NAME_DESC,
  'Latest Created': TemplateOrderBy.CREATED_AT_DESC,
  'Oldest Created': TemplateOrderBy.CREATED_AT_ASC
}

type ScheduleFilter = 'All' | 'Scheduled' | 'Not Scheduled'
const SCHEDULE_FILTER_OPTIONS: ScheduleFilter[] = ['All', 'Scheduled', 'Not Scheduled']

const SCHEDULE_FILTER_TO_PARAM: Record<ScheduleFilter, boolean | undefined> = {
  All: undefined,
  Scheduled: true,
  'Not Scheduled': false
}

interface CustomTemplatesProps {
  refreshKey: number
}

const CustomTemplates: FC<CustomTemplatesProps> = ({ refreshKey }) => {
  const { user } = useAuth()
  const dispatch = useAppDispatch()
  const [templates, setTemplates] = useState<ReportTemplateListItem[]>([])
  const [total, setTotal] = useState(0)
  const [searchValue, setSearchValue] = useState('')
  const [sortBy, setSortBy] = useState<string>('A to Z')
  const [scheduleFilter, setScheduleFilter] = useState<ScheduleFilter>('All')
  const [isLoading, setIsLoading] = useState(false)
  const [downloadingTemplateId, setDownloadingTemplateId] = useState<string | null>(null)
  const [editTemplate, setEditTemplate] = useState<ReportTemplateResponse | null>(null)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [deleteTemplate, setDeleteTemplate] = useState<ReportTemplateListItem | null>(null)
  const [duplicateTemplate, setDuplicateTemplate] = useState<ReportTemplateListItem | null>(null)
  const [viewTemplate, setViewTemplate] = useState<ReportTemplateResponse | null>(null)
  const [isViewDrawerOpen, setIsViewDrawerOpen] = useState(false)
  const [scheduleTemplate, setScheduleTemplate] = useState<ReportTemplateResponse | null>(null)
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false)
  const [stopScheduleTemplate, setStopScheduleTemplate] = useState<ReportTemplateResponse | null>(null)
  const [templateDetails, setTemplateDetails] = useState<Record<string, ReportTemplateResponse>>({})
  const [frameworkData, setFrameworkData] = useState<{ name: string; categories: any[]; framework: string } | null>(null)
  const [validationResults, setValidationResults] = useState<Record<string, ValidateTemplateConfigResponse>>({})
  const [isValidating, setIsValidating] = useState(false)

  const hasPermission = useMemo(() => {
    const allowedRoles = [Role.Owner, Role.Admin, Role.Demo]
    return hasAnyRole(allowedRoles, user)
  }, [user])

  const canEditAnyTemplate = useMemo(() => userHasPermission(Permission.ReportsTemplatesEdit, user), [user])
  const canDeleteAnyTemplate = useMemo(() => userHasPermission(Permission.ReportsTemplatesDelete, user), [user])

  const debouncedSearch = useDebounce(searchValue, 300)

  const validateTemplates = useCallback(async (items: ReportTemplateListItem[]) => {
    if (items.length === 0) {
      setValidationResults({})
      return
    }
    setIsValidating(true)
    const results = await Promise.allSettled(
      items.map((t) => ReportTemplateService.validateTemplate(t.id))
    )
    const newResults: Record<string, ValidateTemplateConfigResponse> = {}
    results.forEach((result, index) => {
      const templateId = items[index].id
      if (result.status === 'fulfilled') {
        newResults[templateId] = result.value
      } else {
        newResults[templateId] = { valid: true }
      }
    })
    setValidationResults(newResults)
    setIsValidating(false)
  }, [])

  const fetchScheduleDetails = useCallback(async (items: ReportTemplateListItem[]) => {
    const scheduledIds = items.filter((t) => t.is_scheduled).map((t) => t.id)
    if (scheduledIds.length === 0) {
      setTemplateDetails({})
      return
    }
    const results = await Promise.allSettled(scheduledIds.map((id) => ReportTemplateService.getTemplate(id)))
    const next: Record<string, ReportTemplateResponse> = {}
    results.forEach((result, idx) => {
      if (result.status === 'fulfilled') {
        next[scheduledIds[idx]] = result.value
      }
    })
    setTemplateDetails(next)
  }, [])

  const fetchTemplates = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await ReportTemplateService.listTemplates({
        search: debouncedSearch || undefined,
        order_by: SORT_MAP[sortBy],
        is_scheduled: SCHEDULE_FILTER_TO_PARAM[scheduleFilter]
      })
      setTemplates(response.items)
      setTotal(response.total)
      validateTemplates(response.items)
      fetchScheduleDetails(response.items)
    } catch (error) {
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: 'Failed to Load Templates',
            description: getApiErrorDescription(error)
          }
        })
      )
    } finally {
      setIsLoading(false)
    }
  }, [debouncedSearch, sortBy, scheduleFilter, dispatch, validateTemplates, fetchScheduleDetails])

  useEffect(() => {
    fetchTemplates()
  }, [fetchTemplates, refreshKey])

  useEffect(() => {
    const fetchFrameworkData = async () => {
      try {
        const tenant = await TenantServiceV3.getTenant()
        const isStandardFramework = ['nist', 'onyxia'].includes(tenant.cyFramework)

        let name: string = tenant.cyFramework
        if (!isStandardFramework) {
          const customFramework = await FrameworkServiceV3.getCustomFramework()
          name = customFramework.title
        }

        const categories = await FrameworkServiceV3.searchFrameworkCategories({ framework: tenant.cyFramework })
        setFrameworkData({ name, categories, framework: tenant.cyFramework })
      } catch {
        // silently fail
      }
    }

    fetchFrameworkData()
  }, [])

  const handleSortChange = (e: any) => {
    setSortBy(e.target.value)
  }

  const handleDownloadFromTemplate = async (template: ReportTemplateListItem) => {
    try {
      setDownloadingTemplateId(template.id)
      await generateAndDownloadFromTemplate(template.id, template.name)
      setTemplates((prev) =>
        prev.map((t) => (t.id === template.id ? { ...t, last_generated: new Date().toISOString() } : t))
      )
    } catch (error) {
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: 'Failed to Generate Report',
            description: getApiErrorDescription(error)
          }
        })
      )
      setTimeout(() => dispatch(closeToast()), 5000)
    } finally {
      setDownloadingTemplateId(null)
    }
  }

  const handleView = async (template: ReportTemplateListItem) => {
    try {
      const fullTemplate = await ReportTemplateService.getTemplate(template.id)
      setViewTemplate(fullTemplate)
      setIsViewDrawerOpen(true)
    } catch (error) {
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: 'Failed to Load Template',
            description: getApiErrorDescription(error)
          }
        })
      )
      setTimeout(() => dispatch(closeToast()), 5000)
    }
  }

  const handleEdit = async (template: ReportTemplateListItem) => {
    try {
      const fullTemplate = await ReportTemplateService.getTemplate(template.id)
      setEditTemplate(fullTemplate)
      setIsEditDialogOpen(true)
    } catch (error) {
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: 'Failed to Load Template',
            description: getApiErrorDescription(error)
          }
        })
      )
      setTimeout(() => dispatch(closeToast()), 5000)
    }
  }

  const handleDuplicateConfirm = async (name: string) => {
    if (!duplicateTemplate) return
    try {
      await ReportTemplateService.duplicateTemplate(duplicateTemplate.id, { name })
      dispatch(
        openToast({
          variant: ToastVariant.success,
          props: {
            text: 'Template Duplicated',
            description: `"${name}" has been created.`
          }
        })
      )
      setTimeout(() => dispatch(closeToast()), 5000)
      setDuplicateTemplate(null)
      fetchTemplates()
    } catch (error) {
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: 'Failed to Duplicate Template',
            description: getApiErrorDescription(error)
          }
        })
      )
      setTimeout(() => dispatch(closeToast()), 5000)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTemplate) return
    try {
      await ReportTemplateService.deleteTemplate(deleteTemplate.id)
      dispatch(
        openToast({
          variant: ToastVariant.success,
          props: {
            text: 'Template Deleted',
            description: `"${deleteTemplate.name}" has been deleted.`
          }
        })
      )
      setTimeout(() => dispatch(closeToast()), 5000)
      setDeleteTemplate(null)
      fetchTemplates()
    } catch (error) {
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: 'Failed to Delete Template',
            description: getApiErrorDescription(error)
          }
        })
      )
      setTimeout(() => dispatch(closeToast()), 5000)
    }
  }

  const handleEditDialogClose = () => {
    setIsEditDialogOpen(false)
    setEditTemplate(null)
  }

  const handleOpenSchedule = async (template: ReportTemplateListItem) => {
    try {
      const fullTemplate = await ReportTemplateService.getTemplate(template.id)
      setScheduleTemplate(fullTemplate)
      setIsScheduleDialogOpen(true)
    } catch (error) {
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: 'Failed to Load Template',
            description: getApiErrorDescription(error)
          }
        })
      )
      setTimeout(() => dispatch(closeToast()), 5000)
    }
  }

  const handleScheduleDialogClose = () => {
    setIsScheduleDialogOpen(false)
    setScheduleTemplate(null)
  }

  const handleScheduled = (updated: ReportTemplateResponse) => {
    setTemplateDetails((prev) => ({ ...prev, [updated.id]: updated }))
    setTemplates((prev) =>
      prev.map((t) => (t.id === updated.id ? { ...t, is_scheduled: Boolean(updated.schedule) } : t))
    )
  }

  const handleRequestStopSchedule = () => {
    if (scheduleTemplate) {
      setStopScheduleTemplate(scheduleTemplate)
      setIsScheduleDialogOpen(false)
    }
  }

  const handleConfirmStopSchedule = async () => {
    if (!stopScheduleTemplate) return
    try {
      await ReportTemplateService.stopSchedule(stopScheduleTemplate.id)
      dispatch(
        openToast({
          variant: ToastVariant.success,
          props: { text: 'Schedule Stopped', description: '' }
        })
      )
      setTimeout(() => dispatch(closeToast()), 5000)
      setStopScheduleTemplate(null)
      setScheduleTemplate(null)
      fetchTemplates()
    } catch (error) {
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: 'Failed to Stop Schedule',
            description: getApiErrorDescription(error)
          }
        })
      )
      setTimeout(() => dispatch(closeToast()), 5000)
    }
  }

  const canScheduleTemplate = useCallback(
    (template: ReportTemplateListItem) => {
      if (hasAnyRole([Role.Owner, Role.Admin], user)) return true
      const detail = templateDetails[template.id]
      const scheduleCreator = detail?.schedule?.created_by
      return Boolean(scheduleCreator && user?.id && scheduleCreator === user.id)
    },
    [templateDetails, user]
  )

  const handleTemplateSaved = async (generatedTemplateId?: string) => {
    await fetchTemplates()
    if (generatedTemplateId) {
      setTemplates((prev) =>
        prev.map((t) =>
          t.id === generatedTemplateId ? { ...t, last_generated: new Date().toISOString() } : t
        )
      )
    }
  }

  const reassignMetricsToNewFramework = async (metrics: string[]) => {
    if (metrics.length === 0) return []
    const details = await Promise.allSettled(metrics.map((id) => CpiServiceV3.getCpiDetails(id)))
    const byCategory = new Map<string, string[]>()
    details.forEach((result, idx) => {
      if (result.status === 'fulfilled' && result.value.category?.id) {
        const categoryId = result.value.category.id
        const list = byCategory.get(categoryId) ?? []
        list.push(metrics[idx])
        byCategory.set(categoryId, list)
      }
    })
    return Array.from(byCategory.entries()).map(([id, newMetrics]) => ({ id, metrics: newMetrics }))
  }

  const changeTemplateFramework = async (templateId: string): Promise<ReportTemplateResponse | null> => {
    if (!frameworkData) return null
    const fullTemplate = await ReportTemplateService.getTemplate(templateId)
    const configuredMetrics = fullTemplate.config.categories.flatMap((cat) => cat.metrics)
    const newCategories = await reassignMetricsToNewFramework(configuredMetrics)
    await ReportTemplateService.updateTemplate(templateId, {
      config: { ...fullTemplate.config, framework: frameworkData.framework, categories: newCategories }
    })
    return ReportTemplateService.getTemplate(templateId)
  }

  return (
    <CustomTemplatesContainer>
      <SectionTitle>Custom Templates</SectionTitle>

      <SearchRow>
        <SearchInput search={searchValue} setSearch={setSearchValue} placeholder="Search" sx={{ width: 280 }} />
        <TrendsChartSelectField
          options={SCHEDULE_FILTER_OPTIONS as unknown as string[]}
          value={scheduleFilter}
          handleChange={(e: any) => setScheduleFilter(e.target.value as ScheduleFilter)}
          placeholder="Filter"
          disabled={false}
          hideSelected
          minWidth="200px"
        />
      </SearchRow>

      <SortRow>
        <ItemCountLabel>Number of items: <ItemCountValue>{total}</ItemCountValue></ItemCountLabel>
        <TemplateSortSelect options={SORT_OPTIONS} value={sortBy} handleChange={handleSortChange} />
      </SortRow>

      {isLoading ? null : templates.length > 0 ? (
        <TemplateCardsContainer>
          {templates.map((template) => {
            const validation = validationResults[template.id]
            const isOwner = Boolean(user?.id && template.created_by === user.id)
            const canEdit = isOwner || canEditAnyTemplate
            const canDelete = isOwner || canDeleteAnyTemplate
            const detail = templateDetails[template.id]
            const schedule = detail?.schedule || undefined
            return (
              <ReportCardV2
                key={template.id}
                reportIcon={<PerformanceReportIcon />}
                title={template.name}
                description={template.description || ''}
                hasPermission={hasPermission}
                isLoading={downloadingTemplateId === template.id}
                onDownload={() => handleDownloadFromTemplate(template)}
                createdDate={formatDate(template.created_at)}
                lastEditedDate={formatDate(template.created_at)}
                lastGeneratedDate={formatDate(template.last_generated)}
                validationResult={validation}
                isValidating={isValidating}
                showSchedule
                schedule={schedule}
                onSchedule={() => handleOpenSchedule(template)}
                canSchedule={canScheduleTemplate(template)}
                onFrameworkChange={async () => {
                  await changeTemplateFramework(template.id)
                  await fetchTemplates()
                }}
                headerActions={
                  <TemplateOptionsMenu
                    onView={() => handleView(template)}
                    onEdit={() => handleEdit(template)}
                    onDuplicate={() => setDuplicateTemplate(template)}
                    onDelete={() => setDeleteTemplate(template)}
                    canEdit={canEdit}
                    canDelete={canDelete}
                  />
                }
              />
            )
          })}
        </TemplateCardsContainer>
      ) : (
        (() => {
          let variant: TemplatesEmptyStateVariant = 'default'
          if (debouncedSearch) {
            variant = 'search'
          } else if (scheduleFilter === 'Scheduled') {
            variant = 'scheduled'
          } else if (scheduleFilter === 'Not Scheduled') {
            variant = 'unscheduled'
          }
          return <TemplatesEmptyState variant={variant} />
        })()
      )}

      <CustomizeReportDialog
        isOpen={isEditDialogOpen}
        onClose={handleEditDialogClose}
        onTemplateSaved={handleTemplateSaved}
        editTemplate={editTemplate}
        validationResult={editTemplate ? validationResults[editTemplate.id] : undefined}
        onFrameworkChange={
          editTemplate
            ? async () => {
                const refreshed = await changeTemplateFramework(editTemplate.id)
                if (refreshed) setEditTemplate(refreshed)
                await fetchTemplates()
              }
            : undefined
        }
      />

      <DuplicateTemplateDialog
        isOpen={Boolean(duplicateTemplate)}
        templateName={duplicateTemplate?.name || ''}
        onClose={() => setDuplicateTemplate(null)}
        onConfirm={handleDuplicateConfirm}
      />

      <DeleteTemplateDialog
        isOpen={Boolean(deleteTemplate)}
        templateName={deleteTemplate?.name || ''}
        onClose={() => setDeleteTemplate(null)}
        onConfirm={handleDeleteConfirm}
      />

      <ViewTemplateDrawer
        isOpen={isViewDrawerOpen}
        onClose={() => {
          setIsViewDrawerOpen(false)
          setViewTemplate(null)
        }}
        template={viewTemplate}
        frameworkData={frameworkData}
        validationResult={viewTemplate ? validationResults[viewTemplate.id] : undefined}
        onFrameworkChange={
          viewTemplate
            ? async () => {
                const refreshed = await changeTemplateFramework(viewTemplate.id)
                if (refreshed) setViewTemplate(refreshed)
                await fetchTemplates()
              }
            : undefined
        }
      />

      <ScheduleReportDialog
        isOpen={isScheduleDialogOpen}
        onClose={handleScheduleDialogClose}
        template={scheduleTemplate}
        onScheduled={handleScheduled}
        onRequestStop={handleRequestStopSchedule}
      />

      <StopScheduleConfirmationDialog
        isOpen={Boolean(stopScheduleTemplate)}
        templateName={stopScheduleTemplate?.name || ''}
        onClose={() => setStopScheduleTemplate(null)}
        onConfirm={handleConfirmStopSchedule}
      />
    </CustomTemplatesContainer>
  )
}

export default CustomTemplates
